import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconTrash, IconLoader, IconAlert } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

interface Product { id: string; name: string; code: string | null; unit: string; category_id: string | null; product_type: string; warehouse_ids: string[]; cost: number | null; }
interface Warehouse { id: string; name: string; }
interface Supplier { id: string; name: string; }
interface Category { id: string; name: string; }
interface InventoryItem { product_id: string; warehouse_id: string; quantity: number; reserved_quantity: number; }

interface Line { product_id: string; product_name: string; quantity: string; unit_cost: string; total_cost: string; }

export default function NewStockReceipt() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { user } = useCurrentUser();
  const allowedTypes = ([
    ["receipt", can(user, "stock_receipts")],
    ["transfer", can(user, "stock_transfers")],
    ["correction", can(user, "stock_corrections")],
  ] as const).filter(([, ok]) => ok).map(([t]) => t);
  const [type, setType] = useState<"receipt" | "transfer" | "correction">(allowedTypes[0] ?? "receipt");
  const [warehouseId, setWarehouseId] = useState("");
  const [sourceWarehouseId, setSourceWarehouseId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [note, setNote] = useState("");
  const [updateCost, setUpdateCost] = useState(true);
  const [lines, setLines] = useState<Line[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [includeOpenOrders, setIncludeOpenOrders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Product[]>("/catalog/products?limit=500").then(r => r.data),
      api.get<Warehouse[]>("/warehouses").then(r => r.data),
      api.get<Supplier[]>("/suppliers").then(r => r.data),
      api.get<Category[]>("/catalog/categories").then(r => r.data),
      api.get<{ stock_include_open_orders: boolean }>("/tenant/profile").then(r => r.data).catch(() => null),
    ]).then(([p, w, s, c, profile]) => {
      setProducts(p);
      setWarehouses(w);
      setSuppliers(s);
      setCategories(c);
      setIncludeOpenOrders(profile?.stock_include_open_orders ?? false);
      // One warehouse = nothing to choose — preselect it (new receipts only;
      // editing a draft keeps the warehouse stored on it).
      if (!id && w.length === 1) setWarehouseId(w[0].id);
      if (id) {
        return api.get<any>(`/stock-receipts/${id}`).then(({ data: r }) => {
          if (r.status !== "draft") { nav("/stock-receipts"); return; }
          setType(r.movement_type);
          setWarehouseId(r.warehouse_id);
          setSourceWarehouseId(r.source_warehouse_id || "");
          setSupplierId(r.supplier_id || "");
          setReceiptNumber(r.receipt_number || "");
          setNote(r.note || "");
          setUpdateCost(!!r.update_product_cost);
          setLines(r.items.map((it: any) => ({
            product_id: it.product_id,
            product_name: it.product_name,
            quantity: String(it.quantity),
            unit_cost: it.unit_cost === null ? "" : String(it.unit_cost / 100),
            total_cost: it.unit_cost === null ? "" : String(Math.round(it.quantity * it.unit_cost) / 100),
          })));
        });
      }
    }).then(() => setLoading(false)).catch(() => {
      setErr(t("stockReceipt.loadError"));
      setLoading(false);
    });
  }, [t]);

  useEffect(() => {
    if (!warehouseId) { setInventory([]); return; }
    api.get<any[]>("/inventory/summary")
      .then(({ data }) => {
        const items: InventoryItem[] = [];
        data.forEach((p: any) => {
          p.levels.forEach((l: any) => {
            items.push({ product_id: p.product_id, warehouse_id: l.warehouse_id, quantity: l.quantity, reserved_quantity: l.reserved_quantity ?? 0 });
          });
        });
        setInventory(items);
      })
      .catch(() => setInventory([]));
  }, [warehouseId]);

  function newLine(product: Product): Line {
    return {
      product_id: product.id,
      product_name: product.name,
      quantity: "",
      unit_cost: product.cost !== null ? String(product.cost / 100) : "",
      total_cost: "",
    };
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    });
  }

  function addLine(productId: string) {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    setLines(prev => [...prev, newLine(product)]);
    scrollToBottom();
  }

  // Only stock-tracked products assigned to the selected warehouse (or
  // unrestricted products) can be added to a stock movement.
  function isStockEligible(p: Product): boolean {
    if (p.product_type !== "stockable" && p.product_type !== "inventory_only") return false;
    return p.warehouse_ids.length === 0 || (warehouseId !== "" && p.warehouse_ids.includes(warehouseId));
  }

  function addCategoryLines(categoryId: string) {
    setLines(prev => [
      ...prev,
      ...products
        .filter(p => p.category_id === categoryId && isStockEligible(p) && !prev.some(l => l.product_id === p.id))
        .map(newLine),
    ]);
    scrollToBottom();
  }

  function updateLine(index: number, field: keyof Line, value: string) {
    setLines(prev => prev.map((l, i) => {
      if (i !== index) return l;
      const next = { ...l, [field]: value };
      const qty = parseFloat(next.quantity) || 0;
      const unit = parseFloat(next.unit_cost) || 0;
      const total = parseFloat(next.total_cost) || 0;
      if (field === "quantity" || field === "unit_cost") {
        next.total_cost = qty && unit ? String(Math.round(qty * unit * 100) / 100) : "";
      } else if (field === "total_cost" && qty) {
        next.unit_cost = total ? String(Math.round((total / qty) * 100) / 100) : "";
      }
      return next;
    }));
  }

  function removeLine(index: number) {
    setLines(prev => prev.filter((_, i) => i !== index));
  }

  function currentStock(productId: string): number | null {
    const item = inventory.find(i => i.product_id === productId && i.warehouse_id === warehouseId);
    if (!item) return null;
    return includeOpenOrders ? item.quantity - item.reserved_quantity : item.quantity;
  }

  function grandTotal(): number {
    return lines.reduce((sum, l) => sum + (parseFloat(l.total_cost) || 0), 0);
  }

  function buildPayload(asDraft: boolean) {
    const totalCost = grandTotal();
    return {
      receipt_number: receiptNumber || null,
      movement_type: type,
      status: asDraft ? "draft" : "posted",
      warehouse_id: warehouseId,
      source_warehouse_id: type === "transfer" ? sourceWarehouseId : null,
      supplier_id: type === "receipt" ? (supplierId || null) : null,
      note: note || null,
      total_cost: type === "receipt" ? Math.round(totalCost * 100) : 0,
      update_product_cost: type === "receipt" && updateCost,
      items: lines.map(l => ({
        product_id: l.product_id,
        quantity: parseFloat(l.quantity) || 0,
        unit_cost: type === "receipt" && l.unit_cost ? Math.round(parseFloat(l.unit_cost) * 100) : null,
      })),
    };
  }

  async function save(asDraft: boolean) {
    setErr(null);
    if (!warehouseId) { setErr(t("stockReceipt.warehouseRequired")); return; }
    if (type === "transfer" && !sourceWarehouseId) { setErr(t("stockReceipt.sourceWarehouseRequired")); return; }
    if (!asDraft && lines.length === 0) { setErr(t("stockReceipt.itemsRequired")); return; }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/stock-receipts/${id}`, buildPayload(true));
        if (!asDraft) await api.post(`/stock-receipts/${id}/finalize`);
      } else {
        await api.post("/stock-receipts", buildPayload(asDraft));
      }
      nav("/stock-receipts");
    } catch (e: any) {
      setErr(apiError(e, t("stockReceipt.saveError")));
    } finally {
      setSaving(false);
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    void save(false);
  }

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? t("stockReceipt.editTitle") : t("stockReceipt.newTitle")}</h1>
        <p>{t("stockReceipt.newSubtitle")}</p>
      </div>

      {err && <div className="card" style={{ padding: 12, marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><IconAlert size={16} /> {err}</div>}

      <form onSubmit={submit} className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginBottom: 24 }}>
          <div>
            <label>{t("stockReceipt.movementType")}</label>
            <select value={type} onChange={e => setType(e.target.value as any)} disabled={isEdit}>
              {allowedTypes.includes("receipt") && <option value="receipt">{t("stockReceipt.typeReceipt")}</option>}
              {allowedTypes.includes("transfer") && <option value="transfer">{t("stockReceipt.typeTransfer")}</option>}
              {allowedTypes.includes("correction") && <option value="correction">{t("stockReceipt.typeCorrection")}</option>}
            </select>
          </div>
          <div>
            <label>{t("stockReceipt.targetWarehouse")} *</label>
            <SearchableSelect value={warehouseId} onChange={setWarehouseId} options={warehouses.map(w => ({ value: w.id, label: w.name }))} placeholder={t("stockReceipt.selectWarehouse")} />
          </div>
          {type === "receipt" && (
            <div>
              <label>{t("stockReceipt.supplier")}</label>
              <SearchableSelect value={supplierId} onChange={setSupplierId} options={suppliers.map(s => ({ value: s.id, label: s.name }))} placeholder={t("stockReceipt.selectSupplier")} />
            </div>
          )}
          {type === "transfer" && (
            <div>
              <label>{t("stockReceipt.sourceWarehouse")} *</label>
              <SearchableSelect value={sourceWarehouseId} onChange={setSourceWarehouseId} options={warehouses.map(w => ({ value: w.id, label: w.name }))} placeholder={t("stockReceipt.selectSourceWarehouse")} />
            </div>
          )}
          <div>
            <label>{t("stockReceipt.receiptNumber")}</label>
            <input value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)} placeholder={t("stockReceipt.receiptNumberPlaceholder")} />
          </div>
          <div>
            <label>{t("stockReceipt.note")}</label>
            <input value={note} onChange={e => setNote(e.target.value)} />
          </div>
          {type === "receipt" && (
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
                <input type="checkbox" checked={updateCost} onChange={e => setUpdateCost(e.target.checked)} style={{ width: "auto" }} />
                {t("stockReceipt.updateCost")}
              </label>
            </div>
          )}
        </div>

        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>{t("stockReceipt.items")}</h3>
        {lines.length === 0 ? (
          <p style={{ color: "var(--muted)", marginBottom: 16 }}>{t("stockReceipt.noItems")}</p>
        ) : (
          <>
            <table style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th>{t("stockReceipt.product")}</th>
                  <th>{t("stockReceipt.currentStock")}</th>
                  <th>{t("stockReceipt.quantity")}</th>
                  <th>{t("stockReceipt.unit")}</th>
                  {type === "receipt" && <th>{t("stockReceipt.unitCost")}</th>}
                  {type === "receipt" && <th>{t("stockReceipt.totalCost")}</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const stock = currentStock(l.product_id);
                  return (
                    <tr key={i}>
                      <td>{l.product_name}</td>
                      <td style={{ color: stock === null ? "var(--muted)" : "var(--text)" }}>{stock === null ? "—" : stock}</td>
                      <td><input type="number" step="0.001" value={l.quantity} onChange={e => updateLine(i, "quantity", e.target.value)} style={{ width: 100 }} /></td>
                      <td>{products.find(p => p.id === l.product_id)?.unit || ""}</td>
                      {type === "receipt" && <td><input type="number" step="0.01" value={l.unit_cost} onChange={e => updateLine(i, "unit_cost", e.target.value)} style={{ width: 120 }} placeholder="0,00" /></td>}
                      {type === "receipt" && <td><input type="number" step="0.01" value={l.total_cost} onChange={e => updateLine(i, "total_cost", e.target.value)} style={{ width: 120 }} placeholder="0,00" /></td>}
                      <td><button type="button" className="ghost" tabIndex={-1} onClick={() => removeLine(i)} style={{ color: "var(--red)" }}><IconTrash size={16} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {type === "receipt" && (
              <div style={{ textAlign: "right", fontSize: 16, fontWeight: 600, marginBottom: 24 }}>
                {t("stockReceipt.grandTotal")}: {grandTotal().toFixed(2)} Kč
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <SearchableSelect
              value=""
              onChange={val => { if (val) { addLine(val); } }}
              options={products.filter(p => isStockEligible(p) && !lines.some(l => l.product_id === p.id)).map(p => ({ value: p.id, label: `${p.name}${p.code ? ` (${p.code})` : ""}` }))}
              placeholder={t("stockReceipt.addProduct")}
              searchPlaceholder={t("stockReceipt.searchProduct")}
              closeOnSelect={false}
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <SearchableSelect
              value=""
              onChange={val => { if (val) { addCategoryLines(val); } }}
              options={categories
                .filter(c => products.some(p => p.category_id === c.id && isStockEligible(p) && !lines.some(l => l.product_id === p.id)))
                .map(c => ({ value: c.id, label: c.name }))}
              placeholder={t("stockReceipt.addCategory")}
              searchPlaceholder={t("stockReceipt.searchCategory")}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" className="primary" disabled={saving}>{saving ? t("common.saving") : t("stockReceipt.save")}</button>
          <button type="button" className="ghost" disabled={saving} onClick={() => void save(true)}>{t("stockReceipt.saveDraft")}</button>
        </div>
      </form>
    </div>
  );
}
